// src/ui/components/streakCelebration.ts — Streak Milestone Celebration Modal (FR-ADHD-005)
// Celebrates milestone practice streaks (3, 5, 10, 20 days) with Clawey dancing animation.

import { tokens } from '../designTokens';
import { ClaweyMascot } from './claweyMascot';
import { MILESTONE_DEFINITIONS, type StreakMilestoneInfo } from '../../storage/streakManager';
import { soundEngine } from '../../audio/soundEngine';

export interface StreakCelebrationOptions {
  milestone: number;
  onDismiss?: () => void;
}

export class StreakCelebration {
  private container: HTMLElement;
  private milestone: number;
  private info: StreakMilestoneInfo;
  private onDismiss?: () => void;
  private mascot: ClaweyMascot;

  constructor(options: StreakCelebrationOptions) {
    this.milestone = options.milestone;
    this.onDismiss = options.onDismiss;
    this.info = MILESTONE_DEFINITIONS[this.milestone] ?? {
      milestone: this.milestone,
      title: `${this.milestone}-Day Voyage!`,
      subtitle: `Magnificent perseverance, Tudor! ${this.milestone} days of natural exploration!`,
      badge: `🌟 ${this.milestone}-Day Streak`,
      rewardNote: 'Awarded Victorian Scholar Seal!',
    };

    this.container = document.createElement('div');
    this.container.id = 'streak-celebration-modal';
    this.container.className = 'streak-celebration-backdrop';

    this.mascot = new ClaweyMascot({
      initialPose: 'dancing',
      size: 160,
    });

    this.render();
    this.playCelebrationEffects();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  private render(): void {
    this.container.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(7, 20, 36, 0.75);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      animation: streakFadeIn 0.3s ease-out;
    `;

    this.container.innerHTML = `
      <style>
        @keyframes streakFadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes floatBadge {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      </style>

      <div class="morris-card" style="
        max-width: 480px;
        width: 100%;
        padding: 36px 30px;
        text-align: center;
        background: #FFFDF9;
        border: 2.5px solid ${tokens.color.gildedBrass};
        border-radius: 24px;
        box-shadow: 0 16px 48px rgba(7, 20, 36, 0.35);
        position: relative;
        overflow: hidden;
      ">
        <!-- Golden Decorative Corner Flourishes -->
        <div style="position: absolute; top: 12px; left: 14px; font-size: 1.2rem; opacity: 0.6;">🪸</div>
        <div style="position: absolute; top: 12px; right: 14px; font-size: 1.2rem; opacity: 0.6;">🧭</div>

        <!-- Floating Milestone Badge -->
        <div style="display: inline-flex; align-items: center; gap: 8px; padding: 6px 18px; border-radius: 20px; background: rgba(229, 169, 60, 0.2); border: 1.5px solid ${tokens.color.gildedBrass}; color: ${tokens.color.morrisIndigo}; font-weight: 800; font-size: 0.95rem; margin-bottom: 12px; animation: floatBadge 3s ease-in-out infinite;">
          ${this.info.badge}
        </div>

        <!-- Mascot Mount (Dancing Pose) -->
        <div id="streak-mascot-mount" style="display: flex; justify-content: center; margin: 8px 0 14px;"></div>

        <!-- Heading & Praise -->
        <h2 style="font-family: ${tokens.font.heading}; font-size: 2rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 8px; line-height: 1.2;">
          ${this.info.title}
        </h2>

        <p style="font-size: 1.05rem; line-height: 1.55; color: #2D3E50; margin: 0 0 20px;">
          ${this.info.subtitle}
        </p>

        <!-- Reward Callout -->
        <div style="padding: 14px 18px; background: rgba(255, 246, 229, 0.9); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 14px; margin-bottom: 24px;">
          <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 2px;">
            Atelier Discovery Unlocked
          </div>
          <div style="font-size: 1rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">
            ✨ ${this.info.rewardNote}
          </div>
        </div>

        <!-- Primary CTA Button (>=48px touch target) -->
        <button
          id="btn-dismiss-streak"
          class="btn-primary touch-target"
          style="width: 100%; min-height: 52px; font-size: 1.15rem; font-weight: 800; border-radius: 16px; cursor: pointer;"
        >
          Magnificent! Onward with the Dive &rarr;
        </button>
      </div>
    `;

    // Mount dancing mascot
    const mount = this.container.querySelector<HTMLElement>('#streak-mascot-mount');
    if (mount) {
      mount.appendChild(this.mascot.getElement());
    }

    // Attach dismissal
    const dismissBtn = this.container.querySelector<HTMLButtonElement>('#btn-dismiss-streak');
    dismissBtn?.addEventListener('click', () => {
      this.close();
    });
  }

  private playCelebrationEffects(): void {
    try {
      soundEngine.playStreak();
    } catch {
      // Audio context might be uninitialized in virtual test DOM
    }
  }

  close(): void {
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    if (this.onDismiss) {
      this.onDismiss();
    }
  }
}
